import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { apiOrigin } from '@/utils/apiBase';

const API = apiOrigin();

/**
 * Public check-in landing page. Guests get a QR code with this URL after
 * their RSVP. Scanning it on arrival:
 *   1. POSTs to /checkin/{token} which marks attendance + enqueues a kiosk welcome
 *   2. Shows them a "Welcome [name]! ✅ Checked in" confirmation
 * Idempotent — scanning twice is fine.
 */
export default function CheckinPage() {
  const { slug, token } = useParams();
  const [status, setStatus] = useState('checking'); // checking | ok | error
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    axios.post(`${API}/api/events/public/${slug}/checkin/${token}`)
      .then(r => { setData(r.data); setStatus('ok'); })
      .catch(e => {
        setErr(e.response?.data?.detail || 'Check-in failed');
        setStatus('error');
      });
  }, [slug, token]);

  if (status === 'checking') return (
    <Centered>
      <div className="w-10 h-10 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-neutral-400 mt-4">Checking you in...</p>
    </Centered>
  );

  if (status === 'error') return (
    <Centered>
      <XCircle size={64} className="text-red-400 mb-3" weight="fill" />
      <h2 className="text-2xl font-bold mb-1">Could not check in</h2>
      <p className="text-sm text-neutral-400">{err}</p>
    </Centered>
  );

  const themeColor = data.theme_color || '#FF6B35';

  return (
    <div className="min-h-screen flex items-center justify-center text-white p-6"
      style={{
        background: `radial-gradient(circle at 50% 0%, ${themeColor}30 0%, transparent 60%), #0a0a0a`,
      }}>
      <div className="max-w-md text-center">
        <div className="text-8xl mb-4 animate-bounce-once">{data.theme_emoji}</div>
        <CheckCircle size={48} weight="fill" className="mx-auto mb-3" style={{ color: themeColor }} />
        <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: themeColor }}>
          {data.already_checked_in ? 'Already checked in' : 'You\'re all set'}
        </p>
        <h1 className="text-4xl font-extrabold mb-2 leading-tight">Welcome,<br /><span style={{ color: themeColor }}>{data.name}</span>!</h1>
        <p className="text-base text-neutral-300 mt-3">{data.event_title}</p>
        <p className="text-sm text-neutral-500 mt-6">
          {data.already_checked_in
            ? 'You can show this screen at the entrance again — your name will light up on the venue display.'
            : 'Look up at the venue display — your name just appeared. Enjoy the celebration!'}
        </p>
      </div>
      <style>{`
        @keyframes bounce-once {
          0%   { transform: scale(0.4); opacity: 0; }
          60%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); }
        }
        .animate-bounce-once { animation: bounce-once 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
      `}</style>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-neutral-950 flex flex-col items-center justify-center text-white p-6 text-center">
      {children}
    </div>
  );
}
